import { FC, useEffect, SyntheticEvent, useState, useMemo, useRef, useCallback } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view';
import { Box, CircularProgress } from '@mui/material';
import { AddOptionsMenu } from './AddOptionsMenu';
import { NodeType, TreeNode as TreeNodeType } from '@/types/tree';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage, getProblemDetail } from '@/lib/utils/apiErrors';
import { TreeNode } from '@/components/tree/TreeNode';
import { TreeErrorBoundary } from '@/components/tree/TreeErrorBoundary';
import ContextMenu from '@/components/tree/ContextMenu';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { useBackend } from '@/hooks/useBackend';
import { useAuthStore } from '@/stores/authStore';
import { getAllAvailableActions, getContextMenuActions } from '@/types/tree/actions';
import { useTreeKeyboardNavigation } from '@/hooks/useTreeKeyboardNavigation';
import { extractEntityIdFromDataId, generateNodeId } from '@/lib/tree/treeUtils';
import { usePrivileges } from '@/hooks/usePrivileges';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { focusTreeNodeInEditMode } from '@/lib/tree/treeNodeEditFocus';
import { selectTreeNode } from '@/lib/tree/selectTreeNode';
import { nodeExpandsOnSelect } from '@/lib/tree/treeNodeExpandOnSelect';
import { useDataActions } from '@/hooks/useDataState';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useActionExecutor } from '@/hooks/useNodeActions';

import { useSnackbar } from '@/hooks/useSnackbar';

/**
 * Props for the TreeArea component
 */
interface BaseTreeAreaProps {
  /** Root node type */
  rootType: NodeType;
  /** Root node ID */
  rootId: string;
  /** Whether BaseTreeArea should call initializeTree on mount */
  autoInitialize?: boolean;
}

/**
 * Generic TreeArea component that manages a tree of nodes.
 * Handles tree state, node expansion, selection, and context menu.
 */
export const BaseTreeArea: FC<BaseTreeAreaProps> = ({ rootType, rootId, autoInitialize = true }) => {
  const { currentUser } = useAuthStore();
  const backend = useBackend();
  // Only get what we actually need from useTree (mostly functions)
  const {
    loadChildren,
    initializeTree,
    setContextMenu,
    clearContextMenu,
    contextMenu,
    areChildrenLoaded,
    getTypedData,
    getNodeData,
  } = useTree();

  // Get individual node actions
  const expandNode = useTreeStore((state) => state.expandNode);
  const collapseNode = useTreeStore((state) => state.collapseNode);

  // Use targeted hooks for better performance
  const { selectedNode } = useTreeSelection();

  // Get whole collections only for MUI SimpleTreeView (unavoidable)
  const nodes = useTreeStore((state) => state.nodes); // ⚠️ For SimpleTreeView only
  const expandedNodes = useTreeStore((state) => state.expandedNodes); // ⚠️ For SimpleTreeView only

  // Targeted subscription for root node loading state
  const rootNodeId = generateNodeId(rootType, rootId);
  const isRootLoaded = useTreeStore((state) => state.loadedNodes.has(rootNodeId));

  const { showSnackbar } = useSnackbar();
  const { isAuthorized } = usePrivileges();

  // Get the action executor - builds dependencies once, takes node as parameter
  const executeActionForNode = useActionExecutor();

  // State for add options menu
  const [addOptionsMenu, setAddOptionsMenu] = useState<{
    type: NodeType;
    node: TreeNodeType;
    position: { x: number; y: number };
  } | null>(null);

  // Track which node was clicked for expansion after successful action
  const [pendingExpansionNodeId, setPendingExpansionNodeId] = useState<string | null>(null);

  // Get root node from store
  const loadingKey = `${rootType}-${rootId}`;
  const rootNode = nodes.get(rootNodeId);
  const { isInitializing } = useDataActions(loadingKey);

  const expandedNodesArray = useMemo(() => Array.from(expandedNodes), [expandedNodes]);

  const treeScrollContainerRef = useRef<HTMLDivElement>(null);
  const missingRootLoggedRef = useRef<string | null>(null);

  /**
   * When selection changes (including programmatic selection from deep links), scroll the tree row
   * into view if it lies outside the scrollable tree panel. Skips scrolling when already visible
   * so normal clicks on nearby nodes do not jump.
   */
  useEffect(() => {
    const nodeId = selectedNode?.nodeId;
    if (!nodeId) {
      return;
    }
    let cancelled = false;
    let retryTimer: number | undefined;
    const scrollSelectedIntoViewIfNeeded = () => {
      const container = treeScrollContainerRef.current;
      if (!container || cancelled) {
        return;
      }
      const item = container.querySelector(`[data-list-tree-item-id="${CSS.escape(nodeId)}"]`);
      if (!item) {
        return;
      }
      const cr = container.getBoundingClientRect();
      const ir = item.getBoundingClientRect();
      if (ir.top < cr.top || ir.bottom > cr.bottom) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        scrollSelectedIntoViewIfNeeded();
        // Deep links may expand the tree a tick later; retry once if the row is not mounted yet.
        retryTimer = window.setTimeout(() => {
          if (!cancelled) {
            scrollSelectedIntoViewIfNeeded();
          }
        }, 150);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
      }
    };
  }, [selectedNode?.nodeId]);

  // Initialize tree on mount
  useEffect(() => {
    if (!autoInitialize) {
      return;
    }
    // Only initialize if not already loaded
    if (!isRootLoaded) {
      const loadRoot = async () => {
        if (!currentUser) {
          return;
        }
        try {
          log.debug('[BaseTreeArea] Initializing tree:', { rootType, rootId });
          const userSettings = currentUser.settings || {};
          await initializeTree(rootType, rootId, userSettings);
          log.debug('[BaseTreeArea] Tree initialized:', () => ({
            rootType,
            rootId,
            isRootLoaded,
            nodeKeys: Array.from(nodes.keys()),
          }));
        } catch (error) {
          log.error('[BaseTreeArea] Error initializing tree:', {
            rootType,
            rootId,
            error: formatErrorMessage(error),
            stack: error instanceof Error ? error.stack : undefined,
          });
        }
      };

      loadRoot();
    }
  }, [rootType, rootId, initializeTree, isRootLoaded, loadingKey, nodes, currentUser, autoInitialize]);

  const isTreeReady = Boolean(rootNode && isRootLoaded);

  const expandNodeOnLabelClick = (nodeId: string, node: TreeNodeType) => {
    if (!nodeExpandsOnSelect(node)) {
      return;
    }
    const store = useTreeStore.getState();
    if (!store.expandedNodes.has(nodeId)) {
      store.expandNode(nodeId);
    }
  };

  /**
   * Handles node selection (label clicks only; chevron clicks are blocked in TreeNode via defaultMuiPrevented).
   */
  const handleNodeSelect = async (
    _event: SyntheticEvent<Element, Event> | null,
    nodeId: string | null
  ): Promise<void> => {
    if (!nodeId) {
      log.warn('[BaseTreeArea] Empty nodeId in handleNodeSelect');
      return;
    }

    log.debug('[BaseTreeArea] Node selected:', { nodeId });
    const node = nodes.get(nodeId);
    if (!node) {
      return;
    }

    if (!node.canBeSelected) {
      expandNodeOnLabelClick(nodeId, node);
      return;
    }

    await selectTreeNode(nodeId, backend);
    expandNodeOnLabelClick(nodeId, node);
  };

  /**
   * Handles node expansion/collapse
   */
  const handleNodeToggle = (_event: SyntheticEvent<Element, Event> | null, nodeIds: string[]): void => {
    log.debug('[BaseTreeArea] Node toggle:', { nodeIds });

    // Find which nodes were expanded and which were collapsed
    const newExpanded = new Set(nodeIds);
    const currentExpanded = new Set(expandedNodes);

    // Handle newly expanded nodes
    nodeIds.forEach((nodeId) => {
      if (!currentExpanded.has(nodeId)) {
        expandNode(nodeId);
      }
    });

    // Handle newly collapsed nodes
    currentExpanded.forEach((nodeId) => {
      if (!newExpanded.has(nodeId)) {
        collapseNode(nodeId);
      }
    });
  };

  const handleAddOption = async (action: string) => {
    if (!addOptionsMenu) return;

    try {
      // Execute the action - node is passed directly, avoiding stale closure issues
      const result = await executeActionForNode(addOptionsMenu.node, action, backend, () => setAddOptionsMenu(null));

      // If the action was successful and we have a pending expansion node, expand it
      if (result && pendingExpansionNodeId) {
        expandNode(pendingExpansionNodeId);
        setPendingExpansionNodeId(null);
      }
    } catch (error) {
      log.error('[BaseTreeArea] Error handling add option:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to add item.', 'error');
    } finally {
      // Clear the pending expansion node regardless of success/failure
      setPendingExpansionNodeId(null);
    }
  };

  const handleContextMenu = async (event: React.MouseEvent, nodeId: string) => {
    // Prevent the default browser context menu
    event.preventDefault();
    event.stopPropagation();

    const node = nodes.get(nodeId);
    if (!node) {
      log.error('[BaseTreeArea] Node not found in store:', { nodeId });
      return;
    }
    if (!areChildrenLoaded(nodeId)) {
      log.debug('[BaseTreeArea] Node children not loaded, loading before showing context menu:', { nodeId });
      await loadChildren(nodeId, backend);
    }

    setContextMenu({
      type: node.type,
      nodeId,
      position: { x: event.clientX, y: event.clientY },
      node,
    });
  };

  const handleAddChild = useCallback(
    async (nodeId: string, event?: React.MouseEvent): Promise<boolean> => {
      const node = nodes.get(nodeId);
      if (!node) {
        log.error('[BaseTreeArea] Node not found:', nodeId);
        return false;
      }

      try {
        // If that node needs data reload, load it first - might influence what actions are available
        if (node.needsDataReload) {
          log.debug('[BaseTreeArea] Node not fully loaded, loading data before checking available actions:', {
            nodeId,
          });
          await loadChildren(nodeId, backend);
        }

        // Get all available actions for this node
        const allActions = getAllAvailableActions(node.type, node, isAuthorized, getTypedData, getNodeData).filter(
          (a) => a.showInAddMenu
        );

        if (allActions.length === 0) {
          log.warn('[BaseTreeArea] No actions available for node type:', node.type);
          return false;
        }

        // If there's only one action, execute it directly
        if (allActions.length === 1) {
          const singleAction = allActions[0];
          log.debug('[BaseTreeArea] Executing single action:', { actionId: singleAction.id, nodeId });
          // Execute action with the node passed directly - no stale closure!
          const result = await executeActionForNode(node, singleAction.id, backend, () => {});
          // Return true if the action was successful (result is truthy)
          return Boolean(result);
        }

        // Get button position for menu placement
        const position = event ? { x: event.clientX, y: event.clientY } : { x: 200, y: 100 };

        setAddOptionsMenu({
          type: node.type,
          node,
          position,
        });

        // Track this node for potential expansion after successful action
        setPendingExpansionNodeId(nodeId);

        // For multiple actions, we show the menu but don't know the result yet
        // The result will be handled by handleAddOption when the user makes a selection
        return false;
      } catch (error) {
        log.error('[BaseTreeArea] Error adding child:', error);
        showSnackbar(getProblemDetail(error) ?? 'Failed to add child item.', 'error');
        return false;
      }
    },
    [nodes, loadChildren, backend, isAuthorized, getTypedData, getNodeData, executeActionForNode, showSnackbar]
  );

  const handleKeyboardReload = useCallback(async () => {
    if (!selectedNode) return;
    const menuActions = getContextMenuActions(selectedNode.type, selectedNode, isAuthorized, getTypedData, getNodeData);
    const reloadAction = menuActions.find((a) => a.id === 'reload');
    if (!reloadAction?.isVisible(selectedNode, isAuthorized, getTypedData)) {
      return;
    }
    await executeActionForNode(selectedNode, 'reload', backend);
  }, [selectedNode, isAuthorized, getTypedData, getNodeData, executeActionForNode, backend]);

  const handleKeyboardAdd = useCallback(async () => {
    if (!selectedNode) return;
    await handleAddChild(selectedNode.nodeId);
  }, [selectedNode, handleAddChild]);

  const handleKeyboardEdit = useCallback(
    (nodeId: string) => {
      const node = getNodeData(nodeId);
      if (node && !nodeIsReadOnlyForUi(node.type)) {
        focusTreeNodeInEditMode(nodeId);
      }
    },
    [getNodeData]
  );

  const handleKeyboardSelect = useCallback(
    (nodeId: string) => {
      void selectTreeNode(nodeId, backend);
    },
    [backend]
  );

  useTreeKeyboardNavigation({
    selectedNodeId: selectedNode?.nodeId ?? null,
    onSelect: handleKeyboardSelect,
    onExpand: expandNode,
    onCollapse: collapseNode,
    onEdit: handleKeyboardEdit,
    onReload: handleKeyboardReload,
    onAdd: handleKeyboardAdd,
  });

  useEffect(() => {
    if (isTreeReady || isInitializing) {
      missingRootLoggedRef.current = null;
      return;
    }

    const logKey = `${autoInitialize}:${rootNodeId}`;
    if (missingRootLoggedRef.current === logKey) {
      return;
    }

    const nodeKeys = Array.from(nodes.keys());
    const rootInStore = nodes.has(rootNodeId);
    const nodesWithSameEntityId = nodeKeys.filter(
      (nodeId) => extractEntityIdFromDataId(nodeId) === rootId && nodeId !== rootNodeId
    );
    const payload = {
      rootType,
      rootId,
      rootNodeId,
      isRootLoaded,
      rootInStore,
      autoInitialize,
      nodesWithSameEntityId,
      nodeKeys,
      loadingKey,
    };

    if (!autoInitialize) {
      missingRootLoggedRef.current = logKey;
      log.error('[BaseTreeArea] Expected root node missing from store (autoInitialize=false):', payload);
      return;
    }

    if (!currentUser) {
      log.debug('[BaseTreeArea] Waiting for user before tree init:', payload);
      return;
    }

    missingRootLoggedRef.current = logKey;
    log.warn('[BaseTreeArea] Root node not ready and tree is not initializing:', payload);
  }, [
    isTreeReady,
    isInitializing,
    rootNodeId,
    isRootLoaded,
    nodes,
    loadingKey,
    autoInitialize,
    rootType,
    rootId,
    currentUser,
  ]);

  return (
    <TreeErrorBoundary>
      {!isTreeReady ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: isInitializing ? 'flex-start' : 'center',
            alignItems: 'flex-start',
            height: '100%',
            width: '100%',
            padding: 2,
          }}
        >
          <CircularProgress size={isInitializing ? 20 : undefined} />
        </Box>
      ) : (
        <Box ref={treeScrollContainerRef} sx={{ height: '100%', overflow: 'auto', position: 'relative' }}>
          <SimpleTreeView
            aria-label="tree view"
            expansionTrigger="iconContainer"
            onExpandedItemsChange={handleNodeToggle}
            onSelectedItemsChange={handleNodeSelect}
            selectedItems={selectedNode?.nodeId || ''}
            expandedItems={expandedNodesArray}
          >
            <TreeNode node={rootNode!} onAddChild={handleAddChild} onContextMenu={handleContextMenu} />
          </SimpleTreeView>
          {contextMenu ? (
            <ContextMenu
              type={contextMenu.type}
              nodeId={contextMenu.nodeId}
              position={contextMenu.position}
              onClose={clearContextMenu}
            />
          ) : null}
          {addOptionsMenu ? (
            <AddOptionsMenu
              node={addOptionsMenu.node}
              position={addOptionsMenu.position}
              onClose={() => setAddOptionsMenu(null)}
              onAddOption={handleAddOption}
            />
          ) : null}
        </Box>
      )}
    </TreeErrorBoundary>
  );
};
